import React from "react";

import {
	Box,
	Chip,
	Icon,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";

import { LoadingComp } from "../../../shared/components/loading/Loading";

import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { IListagemUsuario } from "../../../shared/services/api/user/userService";

const translateUserType = (raw: string) => {
	if (raw === "estate_admin") return "Admin Imobiliária";
	if (raw === "admin") return "Administrador";
	return raw;
};

const userStatusChipSx = (status: string) => {
	switch (status) {
		case "active":
			return {
				color: "#0f8a0d",
				fontWeight: 500,
				background: "#d9f8e5",
				border: "0.5px solid #f1f1f1",
			};
		case "blocked":
			return {
				color: "#202020",
				fontWeight: 500,
				background: "#f0f0f0",
				border: "0.5px solid #f1f1f1",
			};
		default:
			return {
				color: "#e40909",
				fontWeight: 500,
				background: "#fce4e4",
				border: "0.5px solid #f1f1f1",
			};
	}
};

const userStatusLabel = (status: string) => {
	switch (status) {
		case "active":
			return "Ativo";
		case "blocked":
			return "Bloqueado";
		default:
			return "Inativo";
	}
};

interface TableUsersProps {
	rows: IListagemUsuario[];
	isLoading: boolean;
	totalCount: number;
	pagina: number;
	rowsPerPage: number;
	onChangePage: (newPage: number) => void;
	onChangeRowsPerPage: (newRowsPerPage: number) => void;
	onEdit: (userId: string) => void;
}


export const TableUsers: React.FC<TableUsersProps> = ({ rows, isLoading, totalCount, pagina, rowsPerPage, onChangePage, onChangeRowsPerPage, onEdit }) => {
	const canEditUser = checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Update);

	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{
				mt: 1,
				width: "96%",
				px: { xs: 1, sm: 3 },
				py: { xs: 2, sm: 3 },
				ml: 1,
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			<Table size="small" stickyHeader aria-label="tabela de usuários">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						<TableCell>
							<Typography variant="button">Nome</Typography>
						</TableCell>
						<TableCell>
							<Typography variant="button">Cliente</Typography>
						</TableCell>
						<TableCell align="center" colSpan={3}>
							<Typography variant="button">Perfil</Typography>
						</TableCell>
						<TableCell>
							<Typography variant="button">Status</Typography>
						</TableCell>
						<TableCell align="right">
							<Typography variant="button">Ações</Typography>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.length === 0 && !isLoading && (
						<TableRow>
							<TableCell colSpan={7} align="center">
								Nenhum registro
							</TableCell>
						</TableRow>
					)}

					{rows.map((row) => (
						<TableRow key={row.id} hover>
							<TableCell>
								{row.name}
								<Typography variant="body2" color="#878b8b">
									{row.email}
								</Typography>
							</TableCell>

							<TableCell>
								<Typography variant="body2" color="#878b8b">
									{row.client ? row.client.name : ""}
								</Typography>
							</TableCell>

							<TableCell align="center" colSpan={3}>
								<Typography variant="body2" color="#878b8b">
									{translateUserType(row.permission?.description || "não informado")}
								</Typography>
							</TableCell>

							<TableCell>
								<Chip size="medium" label={userStatusLabel(row.status)} sx={userStatusChipSx(row.status)} />
							</TableCell>

							<TableCell align="right">
								<IconButton size="small" onClick={() => onEdit(row.id)} disabled={!canEditUser}>
									<Icon sx={{ color: canEditUser ? "#0170B3" : "action.disabled" }}>app_registration</Icon>
								</IconButton>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			<LoadingComp isopen={isLoading} />

			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={Math.max(pagina - 1, 0)}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onChangePage(newPage + 1)}
					onRowsPerPageChange={(e) => onChangeRowsPerPage(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
